<template>
  <!-- Сводка дня над журналом операций. Раньше это были две одинаковые плашки
       «сегодня» и «прошлый день»: они занимали левую половину полосы, читались
       как один список из шести чисел, и понять по ним, много это или мало, всё
       равно было нельзя — сравнивать приходилось глазом. -->
  <div class="day-stat">
    <div class="day-stat__total">
      <span class="day-stat__caption">Сегодня</span>
      <span class="day-stat__value">{{ totalToday }}</span>
      <span class="day-stat__unit">{{ totalToday === 1 ? 'операция' : 'операций' }}</span>
    </div>

    <div class="day-stat__items">
      <div
        v-for="metric in metrics"
        :key="metric.key"
        class="day-stat__item"
        :class="`day-stat__item--${metric.key}`"
      >
        <span class="day-stat__dot"></span>
        <span class="day-stat__name">{{ metric.name }}</span>
        <span class="day-stat__num">{{ today[metric.key] || 0 }}</span>
        <!-- Сравнение без оценки «хорошо/плохо»: поток груза не заслуга и не
             провал оператора, ему нужен только масштаб. -->
        <span class="day-stat__delta" :title="deltaHint(metric.key)">
          <v-icon size="12">{{ deltaIcon(metric.key) }}</v-icon>
          {{ deltaValue(metric.key) }}
        </span>
      </div>
    </div>

    <!-- Полоса добирает остаток ширины и показывает состав дня целиком:
         числа отвечают «сколько», она — «чего было больше». -->
    <div class="day-stat__bar">
      <template v-if="totalToday">
        <span
          v-for="metric in metrics"
          :key="metric.key"
          class="day-stat__seg"
          :class="`day-stat__item--${metric.key}`"
          :style="{ width: share(metric.key) }"
          :title="`${metric.name}: ${today[metric.key] || 0}`"
        ></span>
      </template>
      <span v-else class="day-stat__empty">за сегодня операций ещё не было</span>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

// Те же три типа и те же цвета, что в фильтре, журнале и графике периода.
const METRICS = [
  { key: 'setting', name: 'Размещение' },
  { key: 'moving', name: 'Перемещение' },
  { key: 'departure', name: 'Убытие' }
]

const DATE_FORMAT = 'DD-MM-YYYY'

export default {
  name: 'DayStatistic',
  data: () => ({
    metrics: METRICS,
    today: {},
    yesterday: {}
  }),
  mounted () {
    this.fetchDay(moment(), 'today')
    this.fetchDay(moment().subtract(1, 'day'), 'yesterday')
  },
  computed: {
    totalToday () {
      return METRICS.reduce((sum, metric) => sum + (this.today[metric.key] || 0), 0)
    }
  },
  methods: {
    delta (key) {
      return (this.today[key] || 0) - (this.yesterday[key] || 0)
    },
    deltaIcon (key) {
      const delta = this.delta(key)

      return delta > 0 ? 'mdi-arrow-up' : delta < 0 ? 'mdi-arrow-down' : 'mdi-equal'
    },
    // При равенстве число не пишем: «−0» читается как отрицательное значение,
    // а знак равенства говорит то же самое и короче.
    deltaValue (key) {
      const delta = this.delta(key)

      return delta === 0 ? '' : `${Math.abs(delta)}`
    },
    deltaHint (key) {
      const delta = this.delta(key)
      const name = METRICS.find(metric => metric.key === key).name
      const was = this.yesterday[key] || 0

      if (delta === 0) return `${name}: столько же, сколько вчера (${was})`

      return `${name}: на ${Math.abs(delta)} ${delta > 0 ? 'больше' : 'меньше'}, чем вчера (${was})`
    },
    share (key) {
      if (!this.totalToday) return '0%'

      return `${((this.today[key] || 0) / this.totalToday) * 100}%`
    },
    fetchDay (date, target) {
      this.$http.get(`/api/operations/statistic?date=${date.format(DATE_FORMAT)}`).then(response => {
        this[target] = response.data
      })
    }
  }
}
</script>

<style scoped>
.day-stat {
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  padding: 8px 16px;
  border: 1px solid var(--sts-line);
  border-radius: 6px;
  background: var(--sts-surface);
}

/* Итог дня — то, ради чего сюда смотрят первым взглядом. */
.day-stat__total {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: none;
}

.day-stat__caption {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--sts-text-muted);
}

.day-stat__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--sts-accent);
}

.day-stat__unit {
  font-size: 11.5px;
  color: var(--sts-text-muted);
}

.day-stat__items {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: none;
}

.day-stat__item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  white-space: nowrap;
}

.day-stat__item--setting { --tone: var(--sts-placed); }
.day-stat__item--moving { --tone: var(--sts-transit); }
.day-stat__item--departure { --tone: var(--sts-out); }

.day-stat__dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: var(--tone);
  align-self: center;
  flex: none;
}

.day-stat__name {
  font-size: 12px;
  color: var(--sts-text-muted);
}

.day-stat__num {
  font-size: 17px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--tone);
}

.day-stat__delta {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--sts-text-muted);
  cursor: default;
}

.day-stat__bar {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 80px;
  height: 8px;
  border-radius: 4px;
  background: var(--sts-line-soft);
  overflow: hidden;
}

.day-stat__seg {
  display: block;
  height: 100%;
  background: var(--tone);
}

.day-stat__empty {
  padding-left: 10px;
  font-size: 11px;
  white-space: nowrap;
  color: var(--sts-text-muted);
}

/* На узком экране полосе места не остаётся — она уходит, а числа остаются:
   перенос строки изменил бы высоту полосы и сдвинул страницу под ней. */
@media (max-width: 1200px) {
  .day-stat__bar {
    display: none;
  }
}

/* Телефон: показатели переносятся под итог, а не уезжают за край. */
@media (max-width: 959px) {
  .day-stat {
    flex-wrap: wrap;
    gap: 8px 16px;
    padding: 8px 10px;
  }

  .day-stat__items {
    flex-wrap: wrap;
    gap: 6px 14px;
  }
}
</style>
